import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { companiesAPI, odooConfigAPI } from '../services/api.ts';
import { Toast } from './Toast.tsx';
import { uploadToCloudinary } from '../services/index.tsx';

interface ProjectFormProps {
  initialValues: {
    _id?: string;
    name: string;
    status?: number;
    company_id?: string | number | { id: number; name: string };
    logo?: File | string;
    color_scheme?: {
      primary: string;
      secondary: string;
    };
    odoo?: {
      url: string;
      db: string;
      username: string;
      password: string;
    };
    odoo_configuration_id?: number;
    admin_email: string;
    admin_password?: string;
    heading?: string;
    sub_heading?: string;
    audit_department_email?: string;
  };
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
}

const ProjectSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Required'),
  status: Yup.number().oneOf([0, 1], 'Invalid status').default(1),
  odoo_configuration_id: Yup.number().required(
    'Odoo configuration is required',
  ),
  company_id: Yup.number().required('Company is required'),
  color_scheme: Yup.object().shape({
    primary: Yup.string(),
    secondary: Yup.string(),
  }),
  admin_email: Yup.string().email('Invalid email').required('Required'),
  admin_password: Yup.string().when('_id', {
    is: (val: string | undefined) => !val,
    then: (schema) =>
      schema
        .min(8, 'Password must be at least 8 characters')
        .required('Required for new project'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const ProjectForm: React.FC<ProjectFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [odooConfigurations, setOdooConfigurations] = useState<any[]>([]);
  const [selectedOdooId, setSelectedOdooId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Find matching Odoo configuration based on URL
  const findOdooConfigId = (configs: any[], username?: string) => {
    if (!username) return null;
    const config = configs.find((c) => c.username === username);
    return config ? config.id : null;
  };

  // Fetch Odoo configurations and set initial values
  useEffect(() => {
    const fetchOdooConfigs = async () => {
      try {
        const response = await odooConfigAPI.getAll();
        setOdooConfigurations(response?.data?.configurations || []);

        // Find matching Odoo configuration based on URL
        if (initialValues.odoo?.username) {
          const matchingConfigId = findOdooConfigId(
            response?.data?.configurations as any[],
            initialValues.odoo.username,
          );

          if (matchingConfigId) {
            setSelectedOdooId(matchingConfigId);
          }
        }
      } catch (error) {
        console.error('Failed to fetch Odoo configurations:', error);
        setApiError('Failed to load Odoo configurations');
      }
    };
    fetchOdooConfigs();

    // eslint-disable-next-line
  }, [initialValues.odoo?.url]);

  // Fetch companies when Odoo configuration is selected
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        if (selectedOdooId) {
          const response = await companiesAPI.getAll(selectedOdooId);
          if (response.success) {
            setCompanies(response.data.companies);
          }
        }
      } catch (error) {
        console.error('Failed to fetch companies:', error);
        setApiError('Failed to load companies');
      } finally {
        setLoading(false);
      }
    };

    if (selectedOdooId) {
      fetchCompanies();
    }
  }, [selectedOdooId]);

  // Update form value when selectedOdooId changes
  const formikRef = useRef<any>(null);
  useEffect(() => {
    if (selectedOdooId && formikRef.current) {
      formikRef.current.setFieldValue('odoo_configuration_id', selectedOdooId);
    }
  }, [selectedOdooId]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        ...initialValues,
        heading: initialValues.heading || '',
        sub_heading: initialValues.sub_heading || '',
        company_id:
          typeof initialValues.company_id === 'object'
            ? (initialValues.company_id as { id: number }).id
            : typeof initialValues.company_id === 'number'
              ? initialValues.company_id
              : Number(initialValues.company_id) || '',
        odoo_configuration_id: '',
        color_scheme: {
          primary: initialValues.color_scheme?.primary || '#000000',
          secondary: initialValues.color_scheme?.secondary || '#000000',
        },
        audit_department_email: initialValues.audit_department_email || '',
      }}
      validationSchema={ProjectSchema}
      onSubmit={async (
        values,
        {
          setSubmitting,
          setErrors,
        }: {
          setSubmitting: (isSubmitting: boolean) => void;
          setErrors: (errors: { submit?: string }) => void;
        },
      ) => {
        try {
          let logoUrl = values.logo;

          // If logo is a File, upload it to Cloudinary first
          if (values.logo instanceof File) {
            try {
              logoUrl = await uploadToCloudinary(values.logo, 'projects');
            } catch (uploadError: any) {
              console.error('Cloudinary upload error:', uploadError);
              throw new Error('Failed to upload logo to Cloudinary');
            }
          }

          const projectData = {
            name: values.name,
            company_id: values.company_id,
            odoo_configuration_id: values.odoo_configuration_id,
            admin_email: values.admin_email,
            audit_department_email: values.audit_department_email,
            admin_password: values.admin_password,
            color_scheme: values.color_scheme,
            logo: logoUrl,
            status: values.status,
            heading: values.heading,
            sub_heading: values.sub_heading,
          };

          // Remove undefined or empty password on update
          if (!projectData.admin_password) {
            delete projectData.admin_password;
          }

          await onSubmit(projectData);
        } catch (error: any) {
          console.error('Form submission error:', error);
          setErrors({
            submit:
              error.response?.data?.message ||
              error.message ||
              'An error occurred during submission',
          });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ setFieldValue, isSubmitting, errors, touched, values }: any) => {
        return (
          <Form className="flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-sm">
            {/* Basic Information Section */}
            <div className="space-y-8 p-6">
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-3 border-b-2 border-gray-100 dark:border-gray-700">
                  <div className="h-1 w-1 rounded-full bg-primary-500"></div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Basic Information
                  </h3>
                </div>
                <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                  {/* Project Name Field */}
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Project Name *
                    </label>
                    <Field
                      name="name"
                      type="text"
                      className={`block w-full rounded-xl border-2 px-4 py-3 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        errors.name && touched.name
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-200 dark:border-gray-600'
                      }`}
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="mt-2 text-sm text-red-600 dark:text-red-400"
                    />
                  </div>

                  {/* Odoo Configuration Field */}
                  <div>
                    <label
                      htmlFor="odoo_configuration_id"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Odoo Configuration *
                    </label>
                    <Field
                      as="select"
                      name="odoo_configuration_id"
                      className={`block w-full rounded-xl border-2 px-4 py-3 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        errors.odoo_configuration_id &&
                        touched.odoo_configuration_id
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-200 dark:border-gray-600'
                      }`}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const value = Number(e.target.value);
                        setSelectedOdooId(value);
                        setFieldValue('odoo_configuration_id', value);
                        setFieldValue('company_id', '');
                      }}
                      value={values.odoo_configuration_id || ''}
                      disabled={loading}
                    >
                      <option value="">Select Odoo Configuration</option>
                      {odooConfigurations.length > 0 &&
                        odooConfigurations.map((config) => (
                          <option key={config.id} value={config.id}>
                            {config.name}
                          </option>
                        ))}
                    </Field>
                    <ErrorMessage
                      name="odoo_configuration_id"
                      component="div"
                      className="mt-2 text-sm text-red-600 dark:text-red-400"
                    />
                  </div>

                  {/* Company Field */}
                  <div>
                    <label
                      htmlFor="company_id"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Company *
                    </label>
                    <Field
                      as="select"
                      name="company_id"
                      className={`block w-full rounded-xl border-2 px-4 py-3 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        errors.company_id && touched.company_id
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-200 dark:border-gray-600'
                      }`}
                      disabled={!selectedOdooId || loading}
                      value={values.company_id}
                    >
                      <option value="">Select a company</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="company_id"
                      component="div"
                      className="mt-2 text-sm text-red-600 dark:text-red-400"
                    />
                    {apiError && (
                      <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                        {apiError}
                      </div>
                    )}
                  </div>

                  {/* Logo Field */}
                  <div>
                    <label
                      htmlFor="logo"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Logo
                    </label>
                    <input
                      id="logo"
                      name="logo"
                      type="file"
                      accept="image/png"
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0];
                        if (file) {
                          setFieldValue('logo', file);
                        }
                      }}
                      className="block w-full text-sm text-gray-600 dark:text-gray-400
                      file:mr-4 file:py-3 file:px-6
                      file:rounded-xl file:border-0
                      file:text-sm file:font-medium
                      file:bg-gradient-to-r file:from-primary-500 file:to-primary-600 file:text-white
                      hover:file:from-primary-600 hover:file:to-primary-700 file:transition-all file:shadow-md hover:file:shadow-lg file:cursor-pointer"
                    />
                  </div>

                  {/* Heading Field */}
                  <div>
                    <label
                      htmlFor="heading"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Heading
                    </label>
                    <Field
                      name="heading"
                      type="text"
                      className="block w-full rounded-xl border-2 border-gray-200 dark:border-gray-600 px-4 py-3 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Sub Heading Field */}
                  <div>
                    <label
                      htmlFor="sub_heading"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Sub Heading
                    </label>
                    <Field
                      name="sub_heading"
                      type="text"
                      className="block w-full rounded-xl border-2 border-gray-200 dark:border-gray-600 px-4 py-3 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Color Scheme Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-3 border-b-2 border-gray-100 dark:border-gray-700">
                  <div className="h-1 w-1 rounded-full bg-purple-500"></div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Color Scheme
                  </h3>
                </div>
                <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                  {/* Primary Color */}
                  <div>
                    <label
                      htmlFor="color_scheme.primary"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Primary Color
                    </label>
                    <div className="flex items-center gap-3">
                      <Field
                        type="text"
                        name="color_scheme.primary"
                        className="flex-1 rounded-xl border-2 border-gray-200 dark:border-gray-600 px-4 py-3 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm dark:bg-gray-700 dark:text-white"
                      />
                      <input
                        type="color"
                        value={values.color_scheme?.primary || '#000000'}
                        onChange={(e) =>
                          setFieldValue('color_scheme.primary', e.target.value)
                        }
                        className="w-14 h-12 p-1 border-2 border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer dark:bg-gray-700"
                      />
                    </div>
                  </div>

                  {/* Secondary Color */}
                  <div>
                    <label
                      htmlFor="color_scheme.secondary"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Secondary Color
                    </label>
                    <div className="flex items-center gap-3">
                      <Field
                        type="text"
                        name="color_scheme.secondary"
                        className="flex-1 rounded-xl border-2 border-gray-200 dark:border-gray-600 px-4 py-3 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm dark:bg-gray-700 dark:text-white"
                      />
                      <input
                        type="color"
                        value={values.color_scheme?.secondary || '#000000'}
                        onChange={(e) =>
                          setFieldValue(
                            'color_scheme.secondary',
                            e.target.value,
                          )
                        }
                        className="w-14 h-12 p-1 border-2 border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer dark:bg-gray-700"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Admin Configuration Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-3 border-b-2 border-gray-100 dark:border-gray-700">
                  <div className="h-1 w-1 rounded-full bg-blue-500"></div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Admin Configuration
                  </h3>
                </div>
                <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                  {/* Admin Email */}
                  <div>
                    <label
                      htmlFor="admin_email"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Admin Email *
                    </label>
                    <Field
                      name="admin_email"
                      type="email"
                      className={`block w-full rounded-xl border-2 px-4 py-3 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        errors.admin_email && touched.admin_email
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-200 dark:border-gray-600'
                      }`}
                    />
                    <ErrorMessage
                      name="admin_email"
                      component="div"
                      className="mt-2 text-sm text-red-600 dark:text-red-400"
                    />
                  </div>

                  {/* Admin Password */}
                  <div>
                    <label
                      htmlFor="admin_password"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Admin Password {!initialValues._id && '*'}
                    </label>
                    <div className="relative">
                      <Field
                        name="admin_password"
                        type={showPassword ? 'text' : 'password'}
                        className={`block w-full rounded-xl border-2 px-4 py-3 pr-12 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                          errors.admin_password && touched.admin_password
                            ? 'border-red-500 dark:border-red-500'
                            : 'border-gray-200 dark:border-gray-600'
                        }`}
                        placeholder={
                          initialValues._id
                            ? 'Leave blank to keep the same password'
                            : ''
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                      >
                        {showPassword ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                    <ErrorMessage
                      name="admin_password"
                      component="div"
                      className="mt-2 text-sm text-red-600 dark:text-red-400"
                    />
                  </div>

                  {/* Audit Department Email */}
                </div>
                <div>
                  <label
                    htmlFor="audit_department_email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Audit Department Email
                  </label>
                  <Field
                    name="audit_department_email"
                    // type="email"
                    className="block w-full rounded-xl border-2 border-gray-200 dark:border-gray-600 px-4 py-3 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-6 mt-6 border-t-2 border-gray-100">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-6 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 border border-transparent rounded-xl shadow-md text-sm font-medium text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg"
                >
                  {isSubmitting
                    ? 'Saving...'
                    : initialValues._id
                      ? 'Update'
                      : 'Create'}
                </button>
              </div>

              {errors?.submit && (
                <div className="mt-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-3">
                  <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                    {errors.submit}
                  </p>
                </div>
              )}

              {toast && (
                <Toast
                  message={toast.message}
                  type={toast.type}
                  onClose={() => setToast(null)}
                />
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
